import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { bookReleaseAPI } from "../api/api";
import { BookRelease } from "../types/book";
import { isAdmin } from "../utils/auth";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const Release: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [release, setRelease] = useState<BookRelease | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRelease = async () => {
      if (!id) return;

      try {
        setLoading(true);
        const releaseData = await bookReleaseAPI.getBookRelease(id);
        setRelease(releaseData);
        setError(null);
      } catch (err) {
        console.error("Error fetching release:", err);
        setError("Failed to load release details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchRelease();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !release) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Release not found"}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{ maxWidth: "100%", mx: "auto", px: { xs: 0, md: 4 } }}
      id="release-root"
    >
      {isAdmin() && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            px: { xs: 2, md: 0 },
          }}
        >
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/book-releases/edit/${id}`)}
            sx={{
              backgroundColor: "#D4AF37",
              "&:hover": {
                backgroundColor: "#B38F2E",
              },
            }}
          >
            Edit Release
          </Button>
        </Box>
      )}

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: { xs: 0, md: 2 },
          border: { xs: "none", md: "1px solid" },
          borderColor: { xs: "transparent", md: "divider" },
          backgroundColor: "background.paper",
          boxShadow: { xs: "none", md: "0 4px 20px rgba(0, 0, 0, 0.05)" },
        }}
      >
        <Grid container spacing={4}>
          {/* Cover Image */}
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={
                release.coverImage ||
                `https://source.unsplash.com/random/400x600?book,${encodeURIComponent(release.title)}`
              }
              alt={`${release.title} cover`}
              sx={{
                width: "100%",
                height: "auto",
                maxHeight: { xs: "50vh", sm: "60vh", md: "600px" },
                objectFit: "contain",
                borderRadius: { xs: 0, md: 1 },
                boxShadow: { xs: "none", md: "0 4px 20px rgba(0, 0, 0, 0.1)" },
              }}
            />
          </Grid>

          {/* Release Details */}
          <Grid item xs={12} md={8}>
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                {release.title}
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontStyle: "italic",
                    color: "text.secondary",
                    mb: 3,
                  }}
                >
                  {release.author}
                </Typography>
              </Typography>

              <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
                <Chip
                  label={release.editionType}
                  color="primary"
                  sx={{
                    borderRadius: 1,
                    fontFamily: '"Playfair Display", serif',
                  }}
                />
                <Chip
                  label={release.status}
                  color={release.status === "Available" ? "success" : "default"}
                  sx={{
                    borderRadius: 1,
                    fontFamily: '"Playfair Display", serif',
                  }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Publisher
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  color: "text.secondary",
                }}
              >
                {release.publisher.name}
              </Typography>

              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Release Date
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  color: "text.secondary",
                }}
              >
                {(() => {
                  const utcDate = dayjs(release.releaseDate).utc();
                  const isTimeSet =
                    utcDate.hour() !== 0 || utcDate.minute() !== 0;
                  const localDate = utcDate.tz(dayjs.tz.guess());
                  return isTimeSet
                    ? `${localDate.format("MMMM D, YYYY[,] h:mm A")} ${dayjs.tz.guess().split("/")[1].replace("_", " ")}`
                    : localDate.format("MMMM D, YYYY");
                })()}
              </Typography>

              {release.price && (
                <>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    Price
                  </Typography>
                  <Typography
                    variant="body1"
                    paragraph
                    sx={{
                      fontFamily: '"Playfair Display", serif',
                      color: "text.secondary",
                    }}
                  >
                    {release.price} {release.currency}
                  </Typography>
                </>
              )}

              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Description
              </Typography>
              <Typography
                variant="body1"
                paragraph
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  color: "text.secondary",
                  whiteSpace: "pre-line",
                }}
              >
                {release.description}
              </Typography>

              {release.preOrderLink && (
                <Box sx={{ mt: 4 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    href={release.preOrderLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="large"
                    sx={{
                      borderRadius: 1,
                      px: 4,
                      py: 1.5,
                      fontWeight: 500,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 6px 25px rgba(0, 0, 0, 0.15)",
                      },
                    }}
                  >
                    Pre-order Now
                  </Button>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default Release;
